import { useState } from "react";
import { toggleFavorite, useFavorites } from "../favorites";
import { useRights } from "../rights";
import type { ApiError } from "../api/client";

/**
 * The two things a reader can do to a record that are theirs alone: mark it,
 * and put it on a list. Shared by the card and by anything else that shows a
 * release, so the heart means the same thing everywhere.
 *
 * The heart flips at once and the request follows; if the API says no the
 * store flips it back and the reason shows for a moment on the button.
 */
export function OwnButtons({ release, label, onAdd }: {
  release: string;
  /** What the picker will say it is adding: "Artist – Title". */
  label: string;
  onAdd: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const favs = useFavorites();
  const rights = useRights();
  const on = favs.has(release);
  const [err, setErr] = useState<string | null>(null);

  // Only the features this reader has. A button for a feature the API would
  // refuse is a button that only ever shows an error.
  return (
    <>
      {rights.has("favorites") && <button
        className={`fav${on ? " on" : ""}`}
        title={err ?? (on ? "unmark this release" : "mark as a favorite")}
        aria-pressed={on}
        onClick={() => {
          setErr(null);
          toggleFavorite(release).catch((e: ApiError) => {
            setErr(e.message);
            setTimeout(() => setErr(null), 3000);
          });
        }}
      >
        {on ? "♥" : "♡"}
      </button>}
      {rights.has("playlists") && (
        <button className="addpl" title={`add every playable track of ${label} to a playlist`} onClick={onAdd}>
          +
        </button>
      )}
      {/* Said out loud, not only in the tooltip: a phone has no tooltips. */}
      {err && <span className="failmsg" role="alert">{err}</span>}
    </>
  );
}
